import React, { useEffect, useState } from "react";
import { Button, Drawer, Form, Input, notification, Radio, Select } from "antd";
import { useTranslation } from "react-i18next";

import "./modalAddCamera.scss";

import { PopupHeaderContainer } from "components/PopupHeader/PopupHeader";
import BasicButtonPrimary from "components/BasicButtonPrimary";

import {
  CAMERA_RESOLUTION_OPTIONS,
  CAMERA_OPTIONS_STATUS,
} from "constants/camera";
import CameraService from "services/cameraService";
import moment from "moment";
import {
  CloseOutlined,
  EyeInvisibleOutlined,
  EyeTwoTone,
  PlusOutlined,
} from "@ant-design/icons";
import { CAMERA_CONNECTION_TYPES } from "constants/camera";
import { CAMERA_CONNECTION_PROTOCOLS } from "constants/camera";

const ModallAddCamera = (props) => {
  const { isOpen, setIsOpen, resetFilter } = props;
  const [form] = Form.useForm();
  const { t: translation } = useTranslation();

  const NUMBER_OF_CONNECTIONS_ALLOW_CREATE = 1;
  // Tạm thời tắt tự động tạo streamUrl.
  // User sẽ tự nhập streamUrl thủ công.
  // Có thể đổi thành true nếu cần dùng lại logic generateRTSPUrl bên dưới.
  const ENABLE_AUTO_GENERATE_STREAM_URL = false;

  const FIELDS_CREATE_STREAM_URL = [
    "brand",
    "ip",
    "username",
    "password",
    "port",
    "connectionChannel",
    "connectionType",
    "connectionProtocol",
  ];

  const [dataFilter, _] = useState({
    filter: {},
    skip: 0,
    limit: 20,
    searchText: undefined,
  });
  // tạm thời mới chỉ 1 kết nối nên dùng như này, nữa nhiều thì cần triển khai thêm
  const [streamUrl, setStreamUrl] = useState("");

  const [dataCreateRTSP, setDataCreateRTSP] = useState(
    FIELDS_CREATE_STREAM_URL.reduce((acc, key) => {
      acc[key] = null;
      return acc;
    }, {})
  );

  const handleCreateCamera = async (values) => {
    try {
      const result = await CameraService.Create(values);
      if (result && result[0]) {
        notification.success({
          message: "",
          description: translation("camera.createSuccess"),
        });
        return result[0]; // Trả về IdStationCamera
      } else {
        throw new Error("Không có kết quả hợp lệ");
      }
    } catch (error) {
      notification.error({
        message: "",
        description: translation("camera.createFailed"),
      });
      return null;
    }
  };

  const handleCreateNewConnection = async (values) => {
    try {
      await CameraService.CreateConnection(values);
      notification.success({
        message: "",
        description: translation("camera.createConnectionSuccess"),
      });
      await form.resetFields();
      setDataCreateRTSP(
        FIELDS_CREATE_STREAM_URL.reduce((acc, key) => {
          acc[key] = null;
          return acc;
        }, {})
      );
      await resetFilter();
      setIsOpen(false);
    } catch (error) {
      notification.error({
        message: "",
        description: translation("camera.createConnectionFailed"),
      });
    }
  };

  const handleCreateNew = async (values) => {
    if (!values?.connections) {
      return notification["error"]({
        message: "",
        description: translation("camera.connectionIsRequired"),
      });
    }
    const keyPayloadCreateConnect = [
      "port",
      "streamUrl",
      "username",
      "password",
    ];
    const payloadCreateConnect = [
      "ipAddress",
      "port",
      "username",
      "password",
      "streamUrl",
      "stationCameraId",
      "connectionName",
      "connectionType",
      "connectionProtocol",
      "connectionChannel",
    ];
    const payloadCreateCamera = Object.fromEntries(
      Object.entries(values).filter(
        ([key]) =>
          key !== "connections" && !keyPayloadCreateConnect.includes(key)
      )
    );
    const payloadCreateConnection = Object.fromEntries(
      Object.entries(values?.connections[0]).filter(([key]) =>
        payloadCreateConnect.includes(key)
      )
    );
    // convert lastActiveAt to the required format
    payloadCreateCamera.lastActiveAt = moment(values.lastActiveAt).format(
      "YYYY-MM-DD HH:mm:ss"
    );
    const stationCameraId = await handleCreateCamera(payloadCreateCamera);
    handleCreateNewConnection({ stationCameraId, ...payloadCreateConnection });
  };

  // Giữ lại logic cũ để sau này có thể bật lại tính năng tự động tạo streamUrl.
  function generateRTSPUrl({
    ipAddress: ip,
    username,
    password,
    port,
    connectionChannel,
    connectionProtocol: protocol = "rtsp",
    subtype = 0, // 0 for main stream, 1 for sub stream
  }) {
    const brand = form.getFieldValue("brand")?.toLowerCase();
    switch (brand) {
      case "hikvision":
        return `${protocol}://${username}:${password}@${ip}:${port}/Streaming/Channels/${connectionChannel}0${
          subtype + 1
        }/`;

      case "dahua":
      case "imou":
        return `${protocol}://${username}:${password}@${ip}:${port}/cam/realmonitor?channel=${connectionChannel}&subtype=${subtype}`;

      case "tplink":
      case "tapo":
        const stream = subtype === 0 ? "stream1" : "stream2";
        return `${protocol}://${username}:${password}@${ip}:${port}/${stream}`;

      default:
        return "Hãng camera chưa được hỗ trợ";
    }
  }

  useEffect(() => {
    if (!ENABLE_AUTO_GENERATE_STREAM_URL) {
      return;
    }

    const isEmpty = Object.values(dataCreateRTSP).every(
      (value) => value === null || value === undefined || value === ""
    );
    if (isEmpty) return;

    const currentConnections = form.getFieldValue("connections");
    if (currentConnections.length > 0) {
      const streamUrl = generateRTSPUrl(dataCreateRTSP);
      form.setFieldsValue({
        connections: [
          {
            streamUrl: streamUrl,
            ...dataCreateRTSP,
          },
        ],
      });
    }
  }, [dataCreateRTSP, ENABLE_AUTO_GENERATE_STREAM_URL]);

  return (
    <Drawer
      visible={isOpen}
      className="drawerManagement"
      title={
        <PopupHeaderContainer
          screenHeaderTitle={translation("camera.cameraAdd")}
          onCloseButtonClick={setIsOpen}
        />
      }
      closable={false}
      onClose={() => setIsOpen(false)}
      width="100vw"
      footer={
        <div className="d-flex w-100 justify-content-end">
          <Button onClick={() => setIsOpen(false)} className="mx-1">
            {translation("camera.cancel")}
          </Button>
          <BasicButtonPrimary
            onclick={() => {
              form.submit();
            }}
            text={translation("camera.save")}
          />
        </div>
      }
    >
      <Form
        form={form}
        onFinish={handleCreateNew}
        layout="vertical"
        initialValues={{
          status: CAMERA_OPTIONS_STATUS[0].value,
          resolution: CAMERA_RESOLUTION_OPTIONS[0].value,
        }}
        onValuesChange={(changedValues, allValues) => {
          if (changedValues.connections) {
            setDataCreateRTSP((prev) => ({
              ...prev,
              ...changedValues.connections[0],
            }));
          }
        }}
      >
        <div className="row p-4">
          <div className="col-12 col-md-12 col-lg-4">
            <h3 className="management-title">
              {translation("camera.generalInformation")}
            </h3>
            <div className="row">
              <div className="col-12 col-md-12">
                <Form.Item
                  name="name"
                  label={translation("camera.name")}
                  rules={[{ required: true, message: translation("isReq") }]}
                >
                  <Input placeholder={translation("camera.name")} />
                </Form.Item>
              </div>
              <div className="col-12 col-md-12">
                <Form.Item
                  name="description"
                  label={translation("camera.description")}
                  rules={[{ required: true, message: translation("isReq") }]}
                >
                  <Input.TextArea
                    placeholder={translation("camera.description")}
                    autoSize={{ minRows: 3, maxRows: 10 }}
                  />
                </Form.Item>
              </div>
              <div className="col-12 col-md-12">
                <Form.Item
                  name="status"
                  label={translation("camera.status")}
                  rules={[{ required: true, message: translation("isReq") }]}
                >
                  <Radio.Group>
                    {CAMERA_OPTIONS_STATUS.map((item) => {
                      return (
                        <Radio key={item.value} value={item.value}>
                          {item.label}
                        </Radio>
                      );
                    })}
                  </Radio.Group>
                </Form.Item>
              </div>
            </div>
          </div>
          <div className="col-12 col-md-12 col-lg-4">
            <h3 className="management-title">
              {translation("camera.technicalProductionInformation")}
            </h3>
            <div className="row">
              <div className="col-12 col-md-12">
                <Form.Item
                  name="brand"
                  label={translation("camera.brand")}
                  rules={[{ required: true, message: translation("isReq") }]}
                >
                  <Input placeholder={translation("camera.brand")} />
                </Form.Item>
              </div>

              <div className="col-12 col-md-12">
                <Form.Item
                  name="model"
                  label={translation("camera.model")}
                  rules={[{ required: true, message: translation("isReq") }]}
                >
                  <Input placeholder={translation("camera.model")} />
                </Form.Item>
              </div>
              <div className="col-12 col-md-12">
                <Form.Item
                  name="serialNumber"
                  label={translation("camera.serialNumber")}
                  rules={[{ required: true, message: translation("isReq") }]}
                >
                  <Input placeholder={translation("camera.serialNumber")} />
                </Form.Item>
              </div>
              <div className="col-12 col-md-12">
                <Form.Item
                  name="resolution"
                  label={translation("camera.resolution")}
                  rules={[{ required: true, message: translation("isReq") }]}
                >
                  <Select
                    size="middle"
                    placeholder={translation("camera.cameraType")}
                    options={CAMERA_RESOLUTION_OPTIONS}
                  />
                </Form.Item>
              </div>
              <div className="col-12 col-md-12">
                <Form.Item
                  name="lens"
                  label={translation("camera.lensType")}
                  rules={[{ required: true, message: translation("isReq") }]}
                >
                  <Input placeholder={translation("camera.lensType")} />
                </Form.Item>
              </div>
              <div className="col-12 col-md-12">
                <Form.Item
                  name="installLocation"
                  label={translation("camera.setupLocation")}
                  rules={[{ required: true, message: translation("isReq") }]}
                >
                  <Input placeholder={translation("camera.setupLocation")} />
                </Form.Item>
              </div>
            </div>
          </div>
          <div className="col-12 col-md-12 col-lg-4">
            <h3 className="management-title">
              {translation("camera.connectionAndStreamInformation")}
            </h3>
            <div>
              <div style={{ height: "30px" }}></div>
              <Form.List name="connections">
                {(fields, { add, remove }) => (
                  <>
                    {fields.map(({ key, name, ...restField }) => (
                      <div
                        key={key}
                        className="connection-block mb-4 p-3 border rounded"
                      >
                        <div className="d-flex justify-content-end align-items-center mb-3">
                          {fields.length > 0 && (
                            <Button
                              type="link"
                              danger
                              onClick={() => remove(name)}
                              icon={<CloseOutlined />}
                            />
                          )}
                        </div>

                        <Form.Item
                          {...restField}
                          name={[name, "connectionName"]}
                          label={translation("camera.connectionName")}
                          rules={[
                            { required: true, message: translation("isReq") },
                          ]}
                        >
                          <Input
                            placeholder={translation("camera.connectionName")}
                          />
                        </Form.Item>

                        <Form.Item
                          {...restField}
                          name={[name, "ipAddress"]}
                          label={translation("camera.ipAddress")}
                          rules={[
                            { required: true, message: translation("isReq") },
                          ]}
                        >
                          <Input
                            placeholder={translation("camera.ipAddress")}
                          />
                        </Form.Item>

                        <Form.Item
                          {...restField}
                          name={[name, "port"]}
                          label={translation("camera.port")}
                          rules={[
                            { required: true, message: translation("isReq") },
                            {
                              pattern: /^[0-9]+$/,
                              message: "Vui lòng chỉ nhập số",
                            },
                            {
                              validator: (_, value) => {
                                if (
                                  value === undefined ||
                                  value === null ||
                                  value === "" ||
                                  !/^[0-9]+$/.test(value)
                                ) {
                                  return Promise.resolve();
                                }

                                const port = Number(value);

                                if (port < 1 || port > 65535) {
                                  return Promise.reject(
                                    new Error(
                                      "Vui lòng nhập cổng từ 1 đến 65535"
                                    )
                                  );
                                }

                                return Promise.resolve();
                              },
                            },
                          ]}
                        >
                          <Input placeholder={translation("camera.port")} />
                        </Form.Item>

                        <Form.Item
                          {...restField}
                          name={[name, "connectionType"]}
                          label={translation("camera.connectionType")}
                          rules={[
                            { required: true, message: translation("isReq") },
                          ]}
                        >
                          <Select
                            placeholder={translation("camera.connectionType")}
                            options={CAMERA_CONNECTION_TYPES}
                          />
                        </Form.Item>

                        <Form.Item
                          {...restField}
                          name={[name, "connectionProtocol"]}
                          label={translation("camera.connectionProtocol")}
                          rules={[
                            { required: true, message: translation("isReq") },
                          ]}
                        >
                          <Select
                            placeholder={translation(
                              "camera.connectionProtocol"
                            )}
                            options={CAMERA_CONNECTION_PROTOCOLS}
                          />
                        </Form.Item>

                        <Form.Item
                          {...restField}
                          name={[name, "connectionChannel"]}
                          label={translation("camera.channel")}
                          rules={[
                            { required: true, message: translation("isReq") },
                          ]}
                        >
                          <Input placeholder={translation("camera.channel")} />
                        </Form.Item>

                        <Form.Item
                          {...restField}
                          name={[name, "streamUrl"]}
                          label={translation("camera.streamUrl")}
                          rules={[
                            { required: true, message: translation("isReq") },
                          ]}
                        >
                          <Input
                            placeholder={translation("camera.streamUrl")}
                          />
                        </Form.Item>

                        <Form.Item
                          {...restField}
                          name={[name, "username"]}
                          label={translation("camera.account")}
                          rules={[
                            { required: true, message: translation("isReq") },
                          ]}
                        >
                          <Input placeholder={translation("camera.account")} />
                        </Form.Item>

                        <Form.Item
                          {...restField}
                          name={[name, "password"]}
                          label={translation("camera.password")}
                          rules={[
                            { required: true, message: translation("isReq") },
                          ]}
                        >
                          <Input.Password
                            placeholder={translation("camera.password")}
                            iconRender={(visible) =>
                              visible ? (
                                <EyeTwoTone />
                              ) : (
                                <EyeInvisibleOutlined />
                              )
                            }
                          />
                        </Form.Item>
                      </div>
                    ))}

                    <Form.Item>
                      <Button
                        type="dashed"
                        block
                        className="d-flex justify-content-center align-items-center"
                        icon={<PlusOutlined />}
                        onClick={() => {
                          if (
                            fields.length >= NUMBER_OF_CONNECTIONS_ALLOW_CREATE
                          ) {
                            notification.error({
                              message: "",
                              description: translation(
                                "camera.maxOneConnectionError",
                                {
                                  number: NUMBER_OF_CONNECTIONS_ALLOW_CREATE,
                                }
                              ),
                            });
                            return;
                          }
                          add();
                        }}
                      >
                        {translation("camera.addConnection")}
                      </Button>
                    </Form.Item>
                  </>
                )}
              </Form.List>
            </div>
          </div>
        </div>
      </Form>
    </Drawer>
  );
};

export default ModallAddCamera;
